import { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, Col, Container, Input, Row } from "reactstrap";
import { Link, withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { withTranslation } from "react-i18next";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import DeleteModal from "../../Components/Common/DeleteModal";
import StockStatusBadge from "./components/StockStatusBadge";
import {
  STOCK_STATUS_OPTIONS,
  VALIDATION_OPTIONS,
  formatPrice,
} from "../../constants/promotedProducts";
import {
  useDeletePromotedProduct,
  useProductCategories,
  usePromotedProducts,
} from "../../hooks/usePromotedProducts";
import { normalizeBackendFileUrl } from "../../utils/backendApi";
import { resolveApiErrorMessage } from "../../utils/resolveApiErrorMessage";
import defaultIllustration from "../../assets/images/lcbtp.png";

const EMPTY_FILTERS = {
  q: "",
  category: "",
  stockStatus: "",
  validated: "",
  startDate: "",
  endDate: "",
};

const PromotedProducts = (props) => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [productToDelete, setProductToDelete] = useState(null);

  const productsQ = usePromotedProducts({ ...filters, page, pageSize: 20 });
  const categoriesQ = useProductCategories();
  const deleteMutation = useDeletePromotedProduct();

  const payload = productsQ.data;
  const products = payload?.results ?? [];
  const count = payload?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / 20));

  const categories = useMemo(() => {
    const data = categoriesQ.data;
    return Array.isArray(data) ? data : data?.results ?? [];
  }, [categoriesQ.data]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, []);

  const handleDelete = () => {
    if (!productToDelete?.id) return;
    deleteMutation.mutate(productToDelete.id, {
      onSettled: () => setProductToDelete(null),
    });
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== "");

  document.title = props.t("Promotion de produits");

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />

      <DeleteModal
        show={Boolean(productToDelete)}
        onDeleteClick={handleDelete}
        onCloseClick={() => setProductToDelete(null)}
      />

      <Container fluid>
        <BreadCrumb
          title={props.t("Promotion de produits")}
          pageTitle={props.t("Promotion de produits")}
        />

        <Row>
          <Col xl={12}>
            <Card>
              <CardBody className="border-bottom">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="card-title mb-0">
                    {count} produit{count > 1 ? "s" : ""}
                  </h5>
                  <div className="d-flex gap-2">
                    <Link
                      to="/promoted-products-pending"
                      className="btn btn-soft-warning"
                    >
                      <i className="ri-time-line align-bottom me-1" />
                      En attente de validation
                    </Link>
                    <Link to="/promoted-products-add" className="btn btn-primary">
                      <i className="ri-add-line align-bottom me-1" />
                      Nouveau produit
                    </Link>
                  </div>
                </div>

                <Row className="g-2">
                  <Col md={3}>
                    <div className="search-box">
                      <Input
                        type="text"
                        className="form-control"
                        placeholder="Rechercher un produit…"
                        value={filters.q}
                        onChange={(e) => handleFilterChange("q", e.target.value)}
                      />
                    </div>
                  </Col>
                  <Col md={2}>
                    <Input
                      type="select"
                      value={filters.category}
                      onChange={(e) =>
                        handleFilterChange("category", e.target.value)
                      }
                    >
                      <option value="">Toutes les catégories</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.designation}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Input
                      type="select"
                      value={filters.stockStatus}
                      onChange={(e) =>
                        handleFilterChange("stockStatus", e.target.value)
                      }
                    >
                      {STOCK_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Input
                      type="select"
                      value={filters.validated}
                      onChange={(e) =>
                        handleFilterChange("validated", e.target.value)
                      }
                    >
                      {VALIDATION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={3}>
                    <div className="d-flex gap-2">
                      <Input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) =>
                          handleFilterChange("startDate", e.target.value)
                        }
                      />
                      <Input
                        type="date"
                        value={filters.endDate}
                        onChange={(e) =>
                          handleFilterChange("endDate", e.target.value)
                        }
                      />
                    </div>
                  </Col>
                </Row>

                {hasActiveFilters && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link mt-2 p-0"
                    onClick={resetFilters}
                  >
                    Réinitialiser les filtres
                  </button>
                )}
              </CardBody>

              <CardBody>
                {productsQ.isLoading ? (
                  // Row skeletons rather than a full-screen spinner.
                  <div className="table-responsive">
                    <table className="table align-middle mb-0">
                      <tbody>
                        {[...Array(5)].map((_, i) => (
                          <tr key={i}>
                            <td colSpan={7}>
                              <div
                                className="placeholder-glow"
                                style={{ height: 40 }}
                              >
                                <span className="placeholder col-12 h-100" />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : productsQ.isError ? (
                  <div className="text-center py-4">
                    <h5>
                      {resolveApiErrorMessage(
                        productsQ.error,
                        "Une erreur est survenue"
                      )}
                    </h5>
                    <button
                      type="button"
                      className="btn btn-primary mt-2"
                      onClick={() => productsQ.refetch()}
                    >
                      Réessayer
                    </button>
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-5">
                    <h5>
                      {hasActiveFilters
                        ? `Aucun résultat${
                            filters.q ? ` pour « ${filters.q} »` : ""
                          }`
                        : "Aucun produit en promotion"}
                    </h5>
                    {hasActiveFilters ? (
                      <button
                        type="button"
                        className="btn btn-light mt-2"
                        onClick={resetFilters}
                      >
                        Réinitialiser les filtres
                      </button>
                    ) : (
                      <Link
                        to="/promoted-products-add"
                        className="btn btn-primary mt-2"
                      >
                        Créer un produit
                      </Link>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table align-middle mb-0">
                        <thead className="table-light text-muted">
                          <tr>
                            <th>IMAGE</th>
                            <th>DÉSIGNATION</th>
                            <th>CATÉGORIE</th>
                            <th>PRIX</th>
                            <th>STOCK</th>
                            <th>STATUT</th>
                            <th>VAL.</th>
                            <th>ACTIONS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map((product) => (
                            <tr key={product.id}>
                              <td>
                                <img
                                  src={
                                    normalizeBackendFileUrl(
                                      product.cover_image
                                    ) || defaultIllustration
                                  }
                                  alt={product.designation}
                                  className="avatar-sm rounded"
                                  style={{ objectFit: "cover" }}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = defaultIllustration;
                                  }}
                                />
                              </td>
                              <td>
                                <Link
                                  to={`/promoted-products-details/${product.id}`}
                                  className="text-dark fw-semibold"
                                >
                                  {product.designation}
                                </Link>
                                {product.brand && (
                                  <p className="text-muted mb-0 fs-12">
                                    {product.brand}
                                  </p>
                                )}
                              </td>
                              <td>{product.category?.designation || "—"}</td>
                              <td className="fw-semibold">
                                {formatPrice(product.price, product.currency)}
                                {product.unit ? (
                                  <span className="text-muted fs-12">
                                    {" "}
                                    / {product.unit}
                                  </span>
                                ) : null}
                              </td>
                              <td>{product.stock_quantity}</td>
                              <td>
                                {/* Displayed as returned by the API — never recomputed. */}
                                <StockStatusBadge
                                  status={product.stock_status}
                                  label={product.stock_status_label}
                                />
                              </td>
                              <td>
                                {product.is_validated ? (
                                  <i
                                    className="ri-checkbox-circle-fill text-success fs-18"
                                    title="Validé"
                                  />
                                ) : (
                                  <i
                                    className="ri-close-circle-fill text-muted fs-18"
                                    title="En attente de validation"
                                  />
                                )}
                              </td>
                              <td>
                                <ul className="list-inline mb-0">
                                  <li className="list-inline-item">
                                    <Link
                                      to={`/promoted-products-details/${product.id}`}
                                      className="text-success d-inline-block"
                                      title="Voir"
                                    >
                                      <i className="ri-eye-fill align-bottom" />
                                    </Link>
                                  </li>
                                  <li className="list-inline-item">
                                    <Link
                                      to={`/promoted-products-update/${product.id}`}
                                      className="text-warning d-inline-block"
                                      title="Modifier"
                                    >
                                      <i className="ri-pencil-fill align-bottom" />
                                    </Link>
                                  </li>
                                  <li className="list-inline-item">
                                    <button
                                      type="button"
                                      className="btn btn-link text-danger p-0"
                                      title="Supprimer"
                                      onClick={() => setProductToDelete(product)}
                                    >
                                      <i className="ri-delete-bin-5-line align-bottom" />
                                    </button>
                                  </li>
                                </ul>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          ‹
                        </button>
                        <span className="text-muted">
                          Page {page} / {totalPages}
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          disabled={page >= totalPages}
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                        >
                          ›
                        </button>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

PromotedProducts.propTypes = {
  t: PropTypes.any,
};

export default withRouter(withTranslation()(PromotedProducts));
